import { CHAT_PROXY_URL, TASK_QUERY_PROXY_URL, ERROR_CODES } from './constants.js';
import { getBackendAuthHeaders } from '../../utils/backendAuth.js';
import { AGENT_CONFIG } from './prompts.js';
import { state, addMessage, updateMessage, dataNormalizer } from './chatUI.js';
import { triggerSolutionLLM, triggerNewQuestionLLM, renderCarouselSelection } from './llmEngine.js';
import { logMessageSubmitted, finalizeChatInteraction } from './tracking.js';

// Internal helper: handles @solution / @newquestion flow (N8N search → carousel or direct LLM)
async function _handleTaskCommand(originalText, sourceCmd, loadingMsg, interactionId, agentType, isCustomAgent) {
    const questionText = originalText.replace(/^(@解答|@solution|@新題目|@newquestion)/i, "").trim();

    try {
        const reqObj1 = {
            Question: questionText,
            task: sourceCmd
        };

        let n8nRes, dbData;
        try {
            n8nRes = await fetch(TASK_QUERY_PROXY_URL, {
                method: "POST",
                headers: await getBackendAuthHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify(reqObj1)
            });
            if (!n8nRes.ok) throw new Error(`HTTP Error ${n8nRes.status}`);
            dbData = await n8nRes.json();
        } catch (fetchErr) {
            updateMessage(loadingMsg, `[System] ERR-201: ${ERROR_CODES['ERR-201']}.`);
            return;
        }

        if (dbData && dbData.status === "error") {
            updateMessage(loadingMsg, `[System] ERR-202: ${ERROR_CODES['ERR-202']}.`);
            return;
        }

        let resultsArray = dataNormalizer(dbData);

        if (!Array.isArray(resultsArray) || resultsArray.length === 0) {
            updateMessage(loadingMsg, "No matching questions found. Please try different keywords.");
            return;
        }

        if (resultsArray.length === 1) {
            const single = resultsArray[0];
            if (sourceCmd === '@solution') {
                // pass the full record so renderer can access chapter/options/etc.
                const renderedResponse = await triggerSolutionLLM(single.question || questionText, single, loadingMsg);
                await finalizeChatInteraction({
                    eventId: interactionId,
                    aiResponse: renderedResponse,
                    isError: false,
                });
            } else {
                const renderedResponse = await triggerNewQuestionLLM(single, loadingMsg);
                await finalizeChatInteraction({
                    eventId: interactionId,
                    aiResponse: renderedResponse,
                    isError: false,
                });
            }
        } else {
            updateMessage(loadingMsg, `Multiple matches found. Please select one:`);
            renderCarouselSelection(resultsArray, loadingMsg, sourceCmd);
            await finalizeChatInteraction({
                eventId: interactionId,
                aiResponse: 'Multiple matches found. User prompted to select one.',
                isError: false,
            });
        }
    } catch (err) { updateMessage(loadingMsg, "Search failed: " + err.message); }
}

// Internal helper: handles generic agent flow (LLM call + optional QueryBuilder mount)
async function _handleAgentCommand(originalText, commandStr, interactionId, agentType, isCustomAgent) {
    const langDirective = state.outputLanguage === 'zh'
        ? "[SYSTEM DIRECTIVE]: Respond EXCLUSIVELY in Traditional Chinese."
        : "[SYSTEM DIRECTIVE]: Respond EXCLUSIVELY in American English.";

    let finalPayload = "";
    let isAgent = false;
    let agentQuestionText = '';
    for (const agent of AGENT_CONFIG) {
        if (agent.regex.test(originalText)) {
            agentQuestionText = originalText.replace(agent.regex, "").trim();
            finalPayload = `${langDirective}\n\n` + agent.getPrompt(agentQuestionText);
            isAgent = true; break;
        }
    }
    if (!isAgent) {
        const modeTag = state.currentMode === 'exploratory' ? '[SYSTEM: EXPLORATORY MODE]' : '[SYSTEM: GENERAL MODE]';
        finalPayload = `${langDirective}\n\n${modeTag} User Question: ${originalText}`;
    }

    const loadingMsg = addMessage("Thinking...", 'bot');
    try {
        const reqObj = { message: finalPayload, mode: "chat" };
        const response = await fetch(CHAT_PROXY_URL, {
            method: "POST",
            headers: await getBackendAuthHeaders({ "Content-Type": "application/json" }),
            body: JSON.stringify(reqObj)
        });
        const data = await response.json();
        const botResponse = data.textResponse || "No response";
        updateMessage(loadingMsg, botResponse);

        await finalizeChatInteraction({
            eventId: interactionId,
            aiResponse: botResponse,
            isError: false,
        });

        if (window.mixpanel) {
            window.mixpanel.track('chat_interaction', {
                mode: state.currentMode,
                agent_type: agentType,
                custom: isCustomAgent,
                timestamp: new Date().toISOString(),
                user_message: originalText,
                ai_response: botResponse,
            });
        }
    } catch (err) {
        const errorMessage = `[System] ERR-301: ${ERROR_CODES['ERR-301']} (${err.message})`;
        updateMessage(loadingMsg, errorMessage);

        await finalizeChatInteraction({
            eventId: interactionId,
            aiResponse: errorMessage,
            isError: true,
        });

        if (window.mixpanel) {
            window.mixpanel.track('chat_interaction', {
                mode: state.currentMode,
                agent_type: agentType,
                custom: isCustomAgent,
                timestamp: new Date().toISOString(),
                user_message: originalText,
                ai_response: errorMessage,
            });
        }
    }
}

// Main entry: parses the input, dispatches to the appropriate flow, manages processing lock.
export async function sendMessage() {
    if (state.isProcessing) return;
    const inputField = document.getElementById('user-input');
    const sendBtn = document.getElementById('send-btn');
    const originalText = inputField.value.trim();
    if (!originalText || !state.currentMode) return;

    state.isProcessing = true;
    inputField.disabled = true;
    if (sendBtn) sendBtn.disabled = true;

    try {
        addMessage(originalText, 'user');
        inputField.value = '';
        document.getElementById('suggestion-box').style.display = 'none';

        let commandStr = state.currentMode === 'exploratory' ? 'Exploratory Mode' : 'General Mode';
        let agentType = null;
        let isCustomAgent = false;
        
        const match = originalText.match(/^(@\S+)/);
        if (match) commandStr = match[1];
        
        // Detect agent type from input
        for (const agent of AGENT_CONFIG) {
            if (agent.regex.test(originalText)) {
                agentType = agent.name;
                isCustomAgent = agent.isCustom || false;
                break;
            }
        }
        
        // Log message submission with tracking
        const interactionId = await logMessageSubmitted({
            originalText,
            mode: state.currentMode,
            agentType,
            isCustomAgent,
        }).catch(err => {
            console.warn('[Tracking] Message logging failed:', err);
            return null;
        });

        if (/^(@解答|@solution|@新題目|@newquestion)/i.test(originalText)) {
            const sourceCmd = /^(@解答|@solution)/i.test(originalText) ? '@solution' : '@newquestion';
            const loadingMsg = addMessage("Connecting to official database...", 'bot');
            await _handleTaskCommand(originalText, sourceCmd, loadingMsg, interactionId, agentType, isCustomAgent);
            return;
        }

        await _handleAgentCommand(originalText, commandStr, interactionId, agentType, isCustomAgent);
    } finally {
        state.isProcessing = false;
        const inputField = document.getElementById('user-input');
        const sendBtn = document.getElementById('send-btn');
        if (inputField) {
            inputField.disabled = false;
            inputField.focus();
        }
        if (sendBtn) sendBtn.disabled = false;
    }
}
